import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../../api/client';
import { FilterControls, TableFilters } from './FilterControls';
import {
  TEXT_DARK, TEXT_BODY, TEXT_SECONDARY, TEXT_MUTED,
  FONT_HEADING, BG_CARD, BORDER_LIGHT,
  ZONE_COLORS, STATUS_COLORS as DESIGN_STATUS_COLORS,
} from './designTokens';

export interface ZoneData {
  id: string;
  name: string;
  colorCode: string;
  sortOrder: number;
}

export interface TableData {
  id: string;
  tableNumber: number;
  tableName: string;
  zone: { name: string; colorCode: string };
  capacity: number;
  status: string;
  reservedGroup: string | null;
  occupancy: number;
}

interface TableMapProps {
  onSelectTable?: (tableId: string) => void;
  refreshKey?: number;
}

const STATUS_LABELS: Record<string, string> = {
  Available: 'Available',
  PartiallyReserved: 'Partial',
  FullyReserved: 'Full',
  Held: 'Held',
  Blocked: 'Blocked',
};

const STATUS_COLORS: Record<string, string> = {
  Available: DESIGN_STATUS_COLORS.Available,
  PartiallyReserved: DESIGN_STATUS_COLORS.PartiallyReserved,
  FullyReserved: DESIGN_STATUS_COLORS.FullyReserved,
  Held: DESIGN_STATUS_COLORS.Held,
  Blocked: DESIGN_STATUS_COLORS.Blocked,
};

const styles: Record<string, React.CSSProperties> = {
  container: {},
  zoneSection: {
    marginBottom: '1.5rem',
  },
  zoneHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    paddingBottom: '0.4rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
  },
  zoneColorDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  zoneName: {
    fontSize: '1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    color: TEXT_DARK,
  },
  zoneCount: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginLeft: '0.5rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '0.75rem',
  },
  card: {
    padding: '0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    cursor: 'pointer',
    transition: 'box-shadow 0.15s, transform 0.1s',
    backgroundColor: BG_CARD,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.4rem',
  },
  tableNumber: {
    fontSize: '1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
  },
  statusBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '0.15rem 0.4rem',
    borderRadius: '3px',
    color: '#fff',
    textTransform: 'uppercase' as const,
  },
  tableName: {
    fontSize: '0.8rem',
    color: TEXT_SECONDARY,
    marginBottom: '0.3rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  occupancy: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_BODY,
  },
  reservedGroup: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
    marginTop: '0.2rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_SECONDARY,
    fontSize: '0.9rem',
  },
  error: {
    color: '#b71c1c',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  retryBtn: {
    padding: '0.35rem 0.75rem',
    backgroundColor: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
};

export function TableMap({ onSelectTable, refreshKey }: TableMapProps) {
  const [tables, setTables] = useState<TableData[]>([]);
  const [zones, setZones] = useState<ZoneData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<TableFilters>({
    zones: [],
    statuses: [],
    minEmptySeats: null,
    reservedGroup: '',
  });
  const lastFetchRef = useRef<number>(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [zonesData, tablesData] = await Promise.all([
        apiClient<ZoneData[]>('/table-management/zones'),
        apiClient<any[]>('/table-management/tables'),
      ]);
      setZones(zonesData.sort((a, b) => a.sortOrder - b.sortOrder));
      // Compute occupancy from seats array returned by API
      const mapped: TableData[] = tablesData.map((t: any) => ({
        ...t,
        occupancy: (t.seats || []).filter((s: any) => s.status === 'Assigned' || s.status === 'CheckedIn').length,
      }));
      setTables(mapped);
      lastFetchRef.current = Date.now();
    } catch {
      setError('Failed to load table data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch when refreshKey changes (triggered by mutations elsewhere)
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchData();
    }
  }, [refreshKey, fetchData]);

  // Auto-refresh on browser focus return (within 3 seconds)
  useEffect(() => {
    function handleFocus() {
      const timeSinceLastFetch = Date.now() - lastFetchRef.current;
      if (timeSinceLastFetch > 3000) {
        fetchData();
      }
    }
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchData]);

  // Apply filters
  const filteredTables = tables.filter((table) => {
    if (filters.zones.length > 0 && !filters.zones.includes(table.zone.name)) {
      return false;
    }
    if (filters.statuses.length > 0 && !filters.statuses.includes(table.status)) {
      return false;
    }
    if (filters.minEmptySeats !== null) {
      const emptySeats = table.capacity - table.occupancy;
      if (emptySeats < filters.minEmptySeats) {
        return false;
      }
    }
    if (filters.reservedGroup.trim()) {
      const search = filters.reservedGroup.toLowerCase();
      if (!table.reservedGroup || !table.reservedGroup.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  // Group tables by zone
  const tablesByZone = zones
    .map((zone) => ({
      zone,
      tables: filteredTables
        .filter((t) => t.zone.name === zone.name)
        .sort((a, b) => a.tableNumber - b.tableNumber),
    }))
    .filter((group) => group.tables.length > 0);

  if (loading) {
    return <div style={styles.loading}>Loading table map...</div>;
  }

  if (error) {
    return (
      <div style={styles.error} role="alert">
        <span>{error}</span>
        <button style={styles.retryBtn} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <FilterControls
        filters={filters}
        onChange={setFilters}
        availableZones={zones.map((z) => ({ name: z.name, colorCode: z.colorCode }))}
      />

      {tablesByZone.length === 0 && (
        <div style={styles.empty}>
          {tables.length === 0
            ? 'No tables configured yet.'
            : 'No tables match the current filters.'}
        </div>
      )}

      {tablesByZone.map(({ zone, tables: zoneTables }) => (
        <div key={zone.id} style={styles.zoneSection}>
          <div style={styles.zoneHeader}>
            <div style={{ ...styles.zoneColorDot, backgroundColor: ZONE_COLORS[zone.name] || zone.colorCode }} />
            <span style={styles.zoneName}>{zone.name}</span>
            <span style={styles.zoneCount}>({zoneTables.length} tables)</span>
          </div>
          <div style={styles.grid}>
            {zoneTables.map((table) => {
              const isFull = table.status === 'FullyReserved';
              return (
              <div
                key={table.id}
                style={{
                  ...styles.card,
                  borderLeft: `4px solid ${ZONE_COLORS[table.zone.name] || table.zone.colorCode}`,
                  ...(isFull ? { backgroundColor: '#e8e8e8', opacity: 0.55, backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)' } : {}),
                }}
                onClick={() => onSelectTable?.(table.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = isFull ? 'inset 0 0 0 1px rgba(26, 107, 60, 0.2)' : 'none';
                  e.currentTarget.style.transform = 'none';
                }}
                role="button"
                tabIndex={0}
                aria-label={`Table ${table.tableNumber} - ${table.tableName}, ${table.occupancy} of ${table.capacity} seats occupied, status: ${STATUS_LABELS[table.status] || table.status}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectTable?.(table.id);
                  }
                }}
              >
                <div style={styles.cardHeader}>
                  <span style={styles.tableNumber}>T{table.tableNumber}</span>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: STATUS_COLORS[table.status] || '#999',
                    }}
                  >
                    {STATUS_LABELS[table.status] || table.status}
                  </span>
                </div>
                <div style={styles.tableName} title={table.tableName}>
                  {table.tableName}
                </div>
                <div style={styles.occupancy}>
                  {table.occupancy} / {table.capacity}
                </div>
                {table.reservedGroup && (
                  <div style={styles.reservedGroup} title={table.reservedGroup}>
                    {table.reservedGroup}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
